/**
 * 2D draw overlay for in-progress shape preview.
 * Uses temporary Google Maps Polygon/Polyline/Circle during drawing.
 */

import type { GeoPoint, DrawingTool } from '@vtak/shared';
import { rectangleFromCorners } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';

/** Overlay stroke color for in-progress drawing */
const OVERLAY_STROKE = '#FFD700';

/** Overlay fill for in-progress drawing */
const OVERLAY_FILL = 'rgba(255, 215, 0, 0.15)';

/** Active overlay polygon */
let overlayPolygon: google.maps.Polygon | null = null;

/** Active overlay polyline */
let overlayPolyline: google.maps.Polyline | null = null;

/** Active overlay circle */
let overlayCircle: google.maps.Circle | null = null;

/** Show the draw overlay for the given vertices and tool */
export function showDrawOverlay2D(
  map: google.maps.Map,
  vertices: GeoPoint[],
  tool: DrawingTool,
): void {
  clearDrawOverlay2D();
  renderOverlay(map, vertices, tool);
}

/** Update the existing draw overlay */
export function updateDrawOverlay2D(
  map: google.maps.Map,
  vertices: GeoPoint[],
  tool: DrawingTool,
): void {
  clearDrawOverlay2D();
  renderOverlay(map, vertices, tool);
}

/** Remove all 2D draw overlay elements */
export function clearDrawOverlay2D(): void {
  if (overlayPolygon) {
    overlayPolygon.setMap(null);
    overlayPolygon = null;
  }
  if (overlayPolyline) {
    overlayPolyline.setMap(null);
    overlayPolyline = null;
  }
  if (overlayCircle) {
    overlayCircle.setMap(null);
    overlayCircle = null;
  }
}

/** Render the appropriate overlay geometry for the tool */
function renderOverlay(map: google.maps.Map, vertices: GeoPoint[], tool: DrawingTool): void {
  if (isCircleTool(tool)) {
    renderCircleOverlay(map, vertices);
    return;
  }

  if (tool === 'rectangle' && vertices.length >= 2) {
    renderRectangleOverlay(map, vertices);
    return;
  }

  if (isPolygonTool(tool) && vertices.length >= 3) {
    renderPolygonOverlay(map, vertices);
    return;
  }

  if (vertices.length >= 2) {
    renderPolylineOverlay(map, vertices);
  }
}

/** Render a circle overlay for circle/geofence tools */
function renderCircleOverlay(map: google.maps.Map, vertices: GeoPoint[]): void {
  if (vertices.length === 0) return;
  const center = vertices[0]!;
  const radius = useDrawingStore.getState().radiusMeters;

  overlayCircle = new google.maps.Circle({
    map,
    center: toLatLng(center),
    radius,
    strokeColor: OVERLAY_STROKE,
    strokeWeight: 2,
    strokeOpacity: 0.8,
    fillColor: OVERLAY_FILL,
    fillOpacity: 0.3,
    clickable: false,
  });
}

/** Render a rectangle overlay from 2 diagonal corners */
function renderRectangleOverlay(map: google.maps.Map, vertices: GeoPoint[]): void {
  const corners = rectangleFromCorners(vertices[0]!, vertices[1]!);
  overlayPolygon = new google.maps.Polygon({
    map,
    paths: corners.map(toLatLng),
    strokeColor: OVERLAY_STROKE,
    strokeWeight: 2,
    strokeOpacity: 0.8,
    fillColor: OVERLAY_FILL,
    fillOpacity: 0.3,
    clickable: false,
  });
}

/** Render a closed polygon overlay */
function renderPolygonOverlay(map: google.maps.Map, vertices: GeoPoint[]): void {
  overlayPolygon = new google.maps.Polygon({
    map,
    paths: vertices.map(toLatLng),
    strokeColor: OVERLAY_STROKE,
    strokeWeight: 2,
    strokeOpacity: 0.8,
    fillColor: OVERLAY_FILL,
    fillOpacity: 0.3,
    clickable: false,
  });
}

/** Render an open polyline overlay */
function renderPolylineOverlay(map: google.maps.Map, vertices: GeoPoint[]): void {
  overlayPolyline = new google.maps.Polyline({
    map,
    path: vertices.map(toLatLng),
    strokeColor: OVERLAY_STROKE,
    strokeWeight: 2,
    strokeOpacity: 0.8,
    clickable: false,
  });
}

/** Convert GeoPoint to Google Maps LatLngLiteral */
function toLatLng(p: GeoPoint): google.maps.LatLngLiteral {
  return { lat: p.lat, lng: p.lon };
}

/** Check if tool renders as a circle */
function isCircleTool(tool: DrawingTool): boolean {
  return tool === 'circle' || tool === 'geofence';
}

/** Check if tool renders as a closed polygon */
function isPolygonTool(tool: DrawingTool): boolean {
  return tool === 'polygon' || tool === 'freeform';
}
